import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { SP_BREAKPOINT } from "../../../constants/app";
import { Client } from "../../../ducks/client";
import { formatDateTime } from "../../../utils/date";
import { Button } from "../../atoms/Button";
import { DatePicker } from "../../atoms/DatePicker";
import { Input } from "../../atoms/Input";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  client: Client;
  onCancel: () => void;
  handleSubmit: (
    clientId: string,
    parentId: string,
    clientName: string,
    validFrom: string,
    validTo: string,
    onError: (message: { [key: string]: string }) => void,
  ) => void;
};

export const ClientEditForm: React.FC<Props> = ({
  client,
  onCancel,
  handleSubmit,
}) => {
  const [errorMessage, setErrorMessage] = useState<{
    [key: string]: string;
  }>({});

  const schema = useMemo(() => {
    return z.object({
      clientId: z.string().min(1),
      parentId: z.string().min(0),
      clientName: z.string().min(1),
      validFrom: z.string().min(0),
      validTo: z.string().min(0),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    unknown,
    FieldValues
  > | null>(null);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        clientId: client.id,
        parentId: client.parentId,
        clientName: client.name,
        validFrom: formatDateTime(
          client.validPeriod.from,
          false,
          "YYYY/MM/DD HH:mm",
        ),
        validTo: formatDateTime(
          client.validPeriod.to,
          false,
          "YYYY/MM/DD HH:mm",
        ),
      }}
      onSubmit={({ clientId, parentId, clientName, validFrom, validTo }) =>
        handleSubmit(
          clientId,
          parentId,
          clientName,
          validFrom,
          validTo,
          setErrorMessage,
        )
      }
    >
      <StyledContainer>
        <StyledInputContainer>
          <StyledInputRowContainer>
            <Input
              label="顧客ID"
              name="clientId"
              fullWidth
              maxLength={12}
              hasError={!!errorMessage["clientId"]}
              errorMessage={errorMessage["clientId"]}
              disabled={true}
            />
            <Input
              label="親顧客ID"
              name="parentId"
              fullWidth
              maxLength={12}
              hasError={!!errorMessage["parentId"]}
              errorMessage={errorMessage["parentId"]}
            />
          </StyledInputRowContainer>

          <Input
            label="顧客名"
            name="clientName"
            required
            maxLength={255}
            hasError={!!errorMessage["clientName"]}
            errorMessage={errorMessage["clientName"]}
          />

          <StyledInputRowContainer>
            <DatePicker
              label="有効期間"
              name="validFrom"
              required
              hasError={!!errorMessage["validFrom"]}
              errorMessage={errorMessage["validFrom"]}
              defaultValue={client.validPeriod.from}
            />
            <DatePicker
              name="validTo"
              hasError={!!errorMessage["validTo"]}
              errorMessage={errorMessage["validTo"]}
              defaultValue={client.validPeriod.to}
            />
          </StyledInputRowContainer>
        </StyledInputContainer>

        <StyledFooter>
          <Button
            text="キャンセル"
            variant="dark"
            type="button"
            onClick={onCancel}
          />
          <Button text="更新" variant="filled" type="submit" />
        </StyledFooter>
      </StyledContainer>
    </FormTemplate>
  );
};

const StyledContainer = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    padding: 0;
    margin-top: 24px;
  }

  padding: 0 10%;
`;

const StyledInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
`;

const StyledInputRowContainer = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    flex-direction: column;
  }

  width: 100%;
  display: flex;
  align-items: baseline;
  gap: 24px;
`;

const StyledFooter = styled.div`
  @media (max-width: ${SP_BREAKPOINT}) {
    justify-content: center;

    button {
      width: 100%;
    }
  }

  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 32px 0;
  border-top: 1px solid #e6e6e6;
`;
